import type { ReactNode } from 'react';

/**
 * A panel with nothing in it, said properly.
 *
 * **An empty portal is the normal state, not an error.** A customer's account is opened by a staff
 * member and re-inviting is also the password recovery path, so somebody can sign in the day they
 * are invited with no case, no appointment and no document — and a dashed grey box with "no data"
 * in it is a poor first impression of a company they are about to trust with a mortgage. Every
 * empty state here therefore says what will happen and, where there is one, offers the next step.
 */
export function EmptyState({
    icon,
    title,
    children,
    action,
    compact = false
}: {
    icon?: ReactNode;
    title: string;
    children?: ReactNode;
    action?: ReactNode;
    /** For a panel sharing a screen with others, where the full height outweighs the content. */
    compact?: boolean;
}) {
    return (
        <div
            className={`flex flex-col items-center rounded-2xl border border-dashed border-slate-300 text-center dark:border-slate-700 ${
                compact ? 'px-5 py-6' : 'px-6 py-10'
            }`}
        >
            {icon !== undefined && (
                <span className="mb-3 flex size-11 items-center justify-center rounded-full bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-400">
                    {icon}
                </span>
            )}

            <p className="font-semibold text-slate-900 dark:text-slate-100">{title}</p>

            {children !== undefined && (
                <p className="mt-1 max-w-sm text-sm text-slate-600 dark:text-slate-400">{children}</p>
            )}

            {action !== undefined && <div className="mt-4">{action}</div>}
        </div>
    );
}
